import { useQuery, useQueryClient } from '@tanstack/react-query';

import { addonDetail } from '@api/sdk.gen';
import {
  AddonLifecycleStatus,
  AddonsAddonListItem,
  AddonsAddonListResponse,
} from '@api/types.gen';

import { withError } from '@/react-tools/react-query';

import { queryKeys } from '../query-keys';

const REFETCH_INTERVAL_WHILE_IN_PROGRESS = 5000;

export function useAddon(id: string) {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: queryKeys.detail(id),
    queryFn: () => getAddon(id),
    enabled: !!id,
    // The list holds the same shape, so seed the first paint from it (if the
    // user came from the list) while the detail refetches in the background.
    placeholderData: () =>
      queryClient
        .getQueryData<AddonsAddonListResponse>(queryKeys.list())
        ?.addons?.find((addon) => addon.id === id),
    refetchInterval: (data) =>
      isAddonInProgress(data) ? REFETCH_INTERVAL_WHILE_IN_PROGRESS : false,
    ...withError('Unable to load add-on'),
  });
}

function isAddonInProgress(data?: AddonsAddonListItem) {
  return (
    data?.lifecycleStatus === AddonLifecycleStatus.ADDON_STATUS_INSTALLING ||
    data?.lifecycleStatus === AddonLifecycleStatus.ADDON_STATUS_UNINSTALLING ||
    data?.lifecycleStatus === AddonLifecycleStatus.ADDON_STATUS_UPGRADING
  );
}

async function getAddon(id: string) {
  const { data } = await addonDetail({ path: { id } });
  return data;
}
